import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircleRounded,
  ContentPasteGoRounded,
  ExpandLessRounded,
  ExpandMoreRounded,
  GavelRounded,
  LibraryAddRounded,
  OpenInNewRounded,
  RestartAltRounded,
  WarningAmberRounded,
} from '@mui/icons-material';

const API_BASE = process.env.REACT_APP_API_BASE || '';
const formatNumber = value => new Intl.NumberFormat('en-CA').format(Number(value || 0));

async function responseJson(response, fallback) {
  let result = {};
  try { result = await response.json(); } catch { /* handled below */ }
  if (response.ok) return result;
  throw new Error(result.error || fallback);
}

function readyForImport(item) {
  return /^\d{4}\s+[A-Z][A-Z0-9]{1,11}\s+\d{1,6}$/.test(String(item.citation || '').trim())
    && String(item.court || '').trim().length > 2
    && String(item.fullText || '').trim().length >= 700;
}

export default function CaseBulkImportDialog({ open, onClose, onImported }) {
  const [rawText, setRawText] = useState('');
  const [preview, setPreview] = useState(null);
  const [cases, setCases] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [expanded, setExpanded] = useState('');
  const [sourceAuthorized, setSourceAuthorized] = useState(false);
  const [reviewConfirmed, setReviewConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const reset = () => {
    setRawText(''); setPreview(null); setCases([]); setSelected(new Set()); setExpanded('');
    setSourceAuthorized(false); setReviewConfirmed(false); setLoading(false); setError(''); setResult(null);
  };

  const close = () => { if (!loading) { reset(); onClose(); } };

  const parseCases = async () => {
    setLoading(true); setError(''); setResult(null);
    try {
      const response = await fetch(`${API_BASE}/api/research/case-import/preview`, {
        method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ rawText }),
      });
      const data = await responseJson(response, 'The copied pages could not be analyzed.');
      const parsed = Array.isArray(data.cases) ? data.cases : [];
      setPreview(data);
      setCases(parsed);
      setSelected(new Set(parsed.filter(item => item.ready && !item.duplicate).map(item => item.previewId)));
      if (!parsed.length) setError('No complete decisions were detected. Copy each entire judgment page, or put “--- CASE ---” between decisions.');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  };

  const updateCase = (previewId, field, value) => {
    setCases(current => current.map(item => {
      if (item.previewId !== previewId) return item;
      const next = { ...item, [field]: value };
      next.ready = readyForImport(next);
      return next;
    }));
  };

  const toggleCase = item => {
    if (item.duplicate || !readyForImport(item)) return;
    setSelected(current => {
      const next = new Set(current);
      if (next.has(item.previewId)) next.delete(item.previewId); else next.add(item.previewId);
      return next;
    });
  };

  const selectedCases = useMemo(() => cases.filter(item => selected.has(item.previewId) && !item.duplicate && readyForImport(item)), [cases, selected]);

  const importCases = async () => {
    setLoading(true); setError('');
    try {
      const response = await fetch(`${API_BASE}/api/research/case-import/commit`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ cases: selectedCases, sourceAuthorized, reviewConfirmed }),
      });
      const data = await responseJson(response, 'The reviewed decisions could not be added.');
      setResult(data);
      onImported?.(data);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="xl" PaperProps={{ sx: { borderRadius: 3, maxHeight: '94vh' } }}>
      <DialogTitle sx={{ pb: 1.25 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box sx={{ width: 42, height: 42, borderRadius: 2.5, display: 'grid', placeItems: 'center', bgcolor: 'primary.main', color: 'primary.contrastText' }}><LibraryAddRounded /></Box>
          <Box sx={{ flex: 1 }}><Typography variant="h6" sx={{ fontWeight: 820 }}>Add copied judgments</Typography><Typography variant="body2" color="text.secondary">Paste up to 100 complete web pages. Lex Maple separates, cleans, labels, deduplicates and queues them for review.</Typography></Box>
          <Chip label={preview ? '2. Review and add' : '1. Paste pages'} color={preview ? 'secondary' : 'primary'} variant="outlined" />
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: { xs: 1.5, md: 2.5 } }}>
        {loading && <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}><CircularProgress size={20} /><Typography variant="body2">{preview ? 'Adding reviewed cases to the searchable library…' : 'Separating and cleaning copied pages…'}</Typography></Stack>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!preview && !result && (
          <Stack spacing={2}>
            <Alert severity="info">Copy the full visible text of each judgment page—including headings and stray navigation. Repeated “CanLII Logo” page starts are separated automatically. For other sources, place <strong>--- CASE ---</strong> on its own line between decisions.</Alert>
            <TextField
              fullWidth multiline minRows={18} maxRows={28} value={rawText} onChange={event => setRawText(event.target.value)}
              placeholder={'Paste one or many complete judgment pages here…\n\nCanLII Logo\nHome\nOntario\nCourt of Appeal for Ontario\n2026 ONCA 728 (CanLII)\n…\n\nCanLII Logo\nHome\nBritish Columbia\n…'}
              inputProps={{ 'aria-label': 'Copied judgment pages' }}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>{formatNumber(rawText.length)} characters · maximum 100 cases / 12 MB per batch</Typography>
              <Button variant="contained" size="large" startIcon={<ContentPasteGoRounded />} disabled={loading || rawText.trim().length < 100} onClick={parseCases}>Clean and categorize</Button>
            </Stack>
          </Stack>
        )}

        {preview && !result && (
          <Stack spacing={2}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', md: 'repeat(5,1fr)' }, gap: 1 }}>
              {[
                ['Pages detected', preview.detectedPages],
                ['Unique cases', cases.length],
                ['Ready to add', cases.filter(item => item.ready && !item.duplicate).length],
                ['Already in library', cases.filter(item => item.duplicate).length],
                ['Repeated copies removed', preview.duplicatesRemoved],
              ].map(([label, value]) => <Paper key={label} variant="outlined" sx={{ p: 1.35, borderRadius: 2.5 }}><Typography sx={{ fontSize: 22, fontWeight: 820 }}>{formatNumber(value)}</Typography><Typography variant="caption" color="text.secondary">{label}</Typography></Paper>)}
            </Box>
            {(preview.rejected || []).length > 0 && <Alert severity="warning">{preview.rejected.length} pasted section{preview.rejected.length === 1 ? '' : 's'} could not be recognized as a complete judgment and will not be added.</Alert>}
            <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ px: 1.5, py: 1.1, bgcolor: 'action.hover' }}>
                <FormControlLabel
                  control={<Checkbox checked={selectedCases.length > 0 && selectedCases.length === cases.filter(item => item.ready && !item.duplicate).length} onChange={event => setSelected(event.target.checked ? new Set(cases.filter(item => item.ready && !item.duplicate).map(item => item.previewId)) : new Set())} />}
                  label={`${selectedCases.length} selected`}
                />
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>Open any row to correct metadata or inspect the cleaned judgment before adding it.</Typography>
                <Button startIcon={<RestartAltRounded />} onClick={() => { setPreview(null); setCases([]); setSelected(new Set()); }}>Paste again</Button>
              </Stack>
              <Box sx={{ maxHeight: 520, overflowY: 'auto' }}>
                {cases.map(item => {
                  const openRow = expanded === item.previewId;
                  const disabled = Boolean(item.duplicate) || !readyForImport(item);
                  return (
                    <Box key={item.previewId} sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ px: 1.25, py: 1.25, bgcolor: selected.has(item.previewId) ? 'action.selected' : 'background.paper' }}>
                        <Checkbox checked={selected.has(item.previewId)} disabled={disabled} onChange={() => toggleCase(item)} inputProps={{ 'aria-label': `Select ${item.name}` }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap alignItems="center">
                            <Typography sx={{ fontWeight: 800 }}>{item.name}</Typography>
                            {item.duplicate && <Chip size="small" color="warning" label="Already in library" />}
                            {!item.duplicate && item.ready && <Chip size="small" color="success" variant="outlined" icon={<CheckCircleRounded />} label="Ready" />}
                            {!item.ready && <Chip size="small" color="warning" icon={<WarningAmberRounded />} label="Review required" />}
                          </Stack>
                          <Typography sx={{ mt: 0.25, fontSize: 13, color: 'primary.main', fontWeight: 760 }}>{item.citation || 'Citation not detected'}</Typography>
                          <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap sx={{ mt: 0.7 }}>
                            <Chip size="small" label={item.jurisdiction || 'Jurisdiction needed'} />
                            <Chip size="small" variant="outlined" label={item.dataset || 'Court code needed'} />
                            {item.documentDate && <Chip size="small" variant="outlined" label={item.documentDate} />}
                            {item.analysis?.primaryCharge?.code !== 'not_classified' && <Chip size="small" color="primary" variant="outlined" label={item.analysis.primaryCharge.label} />}
                            {item.analysis?.outcome?.code !== 'not_classified' && <Chip size="small" variant="outlined" label={item.analysis.outcome.label} />}
                            <Chip size="small" variant="outlined" label={`${formatNumber(item.fullText.length)} cleaned characters`} />
                          </Stack>
                          {(item.warnings || []).length > 0 && <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.65 }}>{item.warnings.join(' ')}</Typography>}
                        </Box>
                        <Tooltip title={openRow ? 'Close review' : 'Review and edit'}><IconButton onClick={() => setExpanded(openRow ? '' : item.previewId)}>{openRow ? <ExpandLessRounded /> : <ExpandMoreRounded />}</IconButton></Tooltip>
                      </Stack>
                      <Collapse in={openRow} unmountOnExit>
                        <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4,minmax(0,1fr))' }, gap: 1.25 }}>
                            <TextField label="Case name" value={item.name} onChange={event => updateCase(item.previewId, 'name', event.target.value)} />
                            <TextField label="Neutral citation" value={item.citation} onChange={event => updateCase(item.previewId, 'citation', event.target.value.toUpperCase())} />
                            <TextField label="Court code" value={item.dataset} onChange={event => updateCase(item.previewId, 'dataset', event.target.value.toUpperCase())} />
                            <TextField label="Decision date" type="date" InputLabelProps={{ shrink: true }} value={item.documentDate} onChange={event => updateCase(item.previewId, 'documentDate', event.target.value)} />
                            <TextField label="Court" value={item.court} onChange={event => updateCase(item.previewId, 'court', event.target.value)} />
                            <TextField label="Province / jurisdiction" value={item.jurisdiction} onChange={event => updateCase(item.previewId, 'jurisdiction', event.target.value)} />
                            <TextField label="Docket / file number" value={item.docket} onChange={event => updateCase(item.previewId, 'docket', event.target.value)} />
                            <TextField label="Official source URL" value={item.sourceUrl} onChange={event => updateCase(item.previewId, 'sourceUrl', event.target.value)} />
                          </Box>
                          <TextField fullWidth multiline minRows={12} maxRows={22} label="Cleaned judgment text" value={item.fullText} onChange={event => updateCase(item.previewId, 'fullText', event.target.value)} sx={{ mt: 1.5 }} />
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}
              </Box>
            </Paper>
            <Alert severity="info">Cases are stored as firm-supplied, review-required authorities. Lex Maple preserves the source link and labels machine-derived categories; it does not treat them as verified holdings or subsequent treatment.</Alert>
            <Stack spacing={0.25}>
              <FormControlLabel control={<Checkbox checked={sourceAuthorized} onChange={event => setSourceAuthorized(event.target.checked)} />} label="The firm is authorized to store and use these judgment copies." />
              <FormControlLabel control={<Checkbox checked={reviewConfirmed} onChange={event => setReviewConfirmed(event.target.checked)} />} label="I reviewed the selected case identities, metadata and cleaned judgment text." />
            </Stack>
          </Stack>
        )}

        {result && (
          <Stack alignItems="center" spacing={1.5} sx={{ py: 5, textAlign: 'center' }}>
            <CheckCircleRounded color="success" sx={{ fontSize: 56 }} />
            <Typography variant="h5" sx={{ fontWeight: 820 }}>{formatNumber(result.imported)} case{result.imported === 1 ? '' : 's'} added</Typography>
            <Typography color="text.secondary">They are indexed in the searchable case library now. {result.skipped ? `${formatNumber(result.skipped)} duplicate cases were safely skipped.` : ''}</Typography>
            {(result.cases || []).slice(0, 6).map(item => <Button key={item.id} component="a" href={`/research?view=decisions&case=${encodeURIComponent(item.id)}&title=${encodeURIComponent(item.name)}&citation=${encodeURIComponent(item.citation)}`} endIcon={<OpenInNewRounded />}>{item.citation} · {item.name}</Button>)}
          </Stack>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button onClick={close}>{result ? 'Done' : 'Cancel'}</Button>
        {preview && !result && <Button variant="contained" startIcon={loading ? <CircularProgress size={17} color="inherit" /> : <GavelRounded />} disabled={loading || !selectedCases.length || !sourceAuthorized || !reviewConfirmed} onClick={importCases}>Add {selectedCases.length} reviewed case{selectedCases.length === 1 ? '' : 's'}</Button>}
      </DialogActions>
    </Dialog>
  );
}
